<template>
  <Teleport to="body">
    <Transition name="fade">
      <div v-if="open" class="cmdk-backdrop" @click.self="close">
        <div class="cmdk-modal">
          <div class="cmdk-search">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6"
                 stroke-linecap="round" stroke-linejoin="round">
              <circle cx="7" cy="7" r="5" /><path d="M11 11l3 3" />
            </svg>
            <input ref="inputRef" v-model="q" placeholder="搜索数据集、用户、数据 …"
                   @keydown.esc="close"
                   @keydown.enter="onEnter"
                   @keydown.down.prevent="moveSel(1)"
                   @keydown.up.prevent="moveSel(-1)" />
            <span class="cmdk-kbd">Esc</span>
          </div>
          <div class="cmdk-body">
            <div v-if="loading" class="cmdk-empty">搜索中…</div>
            <div v-else-if="!q" class="cmdk-empty">输入关键字开始搜索</div>
            <div v-else-if="totalHits === 0" class="cmdk-empty">没有匹配结果</div>
            <template v-else>
              <div v-for="g in groups" :key="g.key" class="cmdk-group">
                <div class="cmdk-group-head">{{ g.label }}</div>
                <div v-for="(hit, i) in g.items" :key="hit.kind + ':' + hit.id"
                     class="cmdk-row" :class="{ active: hit === activeHit }"
                     @mouseenter="setActive(hit)"
                     @click="goto(hit)">
                  <span class="cmdk-icon" :class="hit.kind">
                    {{ kindIcon(hit.kind) }}
                  </span>
                  <div class="cmdk-text">
                    <div class="t">{{ hit.title }}</div>
                    <div class="s" v-if="hit.subtitle">{{ hit.subtitle }}</div>
                  </div>
                  <span class="cmdk-hint" v-if="hit.hint">{{ hit.hint }}</span>
                </div>
              </div>
            </template>
          </div>
          <div class="cmdk-foot">
            <span><kbd>↑</kbd><kbd>↓</kbd> 选择</span>
            <span><kbd>↵</kbd> 跳转</span>
            <span><kbd>Esc</kbd> 关闭</span>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script lang="ts" setup>
  import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
  import { useRouter } from 'vue-router';
  import { globalSearch, SearchHit, SearchResponse } from '/@/api/business/admin';

  const router = useRouter();

  const open = ref(false);
  const q = ref('');
  const loading = ref(false);
  const inputRef = ref<HTMLInputElement | null>(null);
  const result = ref<SearchResponse>({ datasets: [], users: [], data: [] });
  const activeHit = ref<SearchHit | null>(null);

  const groups = computed(() => [
    { key: 'datasets', label: '数据集',     items: result.value.datasets },
    { key: 'users',    label: '用户',       items: result.value.users },
    { key: 'data',     label: '数据 / 帧', items: result.value.data },
  ].filter(g => g.items.length > 0));

  const allHits = computed<SearchHit[]>(() =>
    groups.value.flatMap(g => g.items));
  const totalHits = computed(() => allHits.value.length);

  function kindIcon(k: SearchHit['kind']) {
    return k === 'dataset' ? '📚' : k === 'user' ? '👤' : '🎞';
  }

  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  watch(q, (v) => {
    if (debounceTimer) clearTimeout(debounceTimer);
    if (!v.trim()) {
      result.value = { datasets: [], users: [], data: [] };
      activeHit.value = null;
      return;
    }
    loading.value = true;
    debounceTimer = setTimeout(async () => {
      try {
        result.value = await globalSearch(v.trim());
        activeHit.value = allHits.value[0] || null;
      } catch {
        result.value = { datasets: [], users: [], data: [] };
      } finally {
        loading.value = false;
      }
    }, 200);
  });

  function setActive(hit: SearchHit) { activeHit.value = hit; }
  function moveSel(delta: number) {
    if (totalHits.value === 0) return;
    const arr = allHits.value;
    const i = activeHit.value ? arr.indexOf(activeHit.value) : -1;
    const n = (i + delta + arr.length) % arr.length;
    activeHit.value = arr[n];
  }
  function goto(hit: SearchHit) {
    if (!hit.link) { close(); return; }
    // Hash routes — strip the leading "/#" to use router.push.
    const path = hit.link.startsWith('/#') ? hit.link.slice(2) : hit.link;
    close();
    router.push(path);
  }
  function onEnter() {
    if (activeHit.value) goto(activeHit.value);
  }
  function close() {
    open.value = false;
    q.value = '';
    result.value = { datasets: [], users: [], data: [] };
  }
  function show() {
    open.value = true;
    nextTick(() => inputRef.value?.focus());
  }

  function onKey(e: KeyboardEvent) {
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault();
      open.value ? close() : show();
    }
  }
  onMounted(() => window.addEventListener('keydown', onKey));
  onBeforeUnmount(() => window.removeEventListener('keydown', onKey));

  // Expose open/close for the topbar trigger to call.
  defineExpose({ show, close });
</script>

<style lang="less" scoped>
  .cmdk-backdrop {
    position: fixed; inset: 0;
    background: rgba(0, 0, 0, 0.55);
    backdrop-filter: blur(4px);
    z-index: 9999;
    display: flex; justify-content: center;
    padding-top: 14vh;
  }
  .cmdk-modal {
    width: 640px; max-width: calc(100vw - 32px);
    background: #11161e;
    border: 1px solid rgba(255,255,255,0.12);
    border-radius: 12px;
    box-shadow: 0 20px 60px rgba(0,0,0,0.5);
    overflow: hidden;
    display: flex; flex-direction: column;
    max-height: 70vh;
  }
  .cmdk-search {
    display: flex; align-items: center; gap: 10px;
    padding: 14px 16px;
    border-bottom: 1px solid rgba(255,255,255,0.08);
    color: #C8D1DB;
    svg { width: 16px; height: 16px; flex-shrink: 0; opacity: 0.6; }
    input {
      flex: 1;
      background: transparent;
      border: none; outline: none;
      color: #F4F7FA;
      font-size: 15px;
      font-family: inherit;
      &::placeholder { color: #5C6B7C; }
    }
  }
  .cmdk-kbd {
    background: rgba(255,255,255,0.08);
    color: #C8D1DB;
    padding: 2px 7px;
    border-radius: 4px;
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    letter-spacing: 0.04em;
    border: 1px solid rgba(255,255,255,0.06);
  }
  .cmdk-body {
    overflow-y: auto;
    padding: 6px 0;
    flex: 1;
  }
  .cmdk-empty {
    padding: 32px 18px;
    text-align: center;
    color: #5C6B7C;
    font-size: 13px;
  }
  .cmdk-group { padding: 4px 0; }
  .cmdk-group-head {
    padding: 6px 16px;
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: #5C6B7C;
    font-weight: 600;
  }
  .cmdk-row {
    display: grid;
    grid-template-columns: 28px 1fr auto;
    align-items: center;
    gap: 12px;
    padding: 8px 16px;
    cursor: pointer;
    color: #C8D1DB;
    transition: background 80ms ease;
    &.active, &:hover {
      background: rgba(74,149,181,0.16);
      color: #F4F7FA;
    }
  }
  .cmdk-icon {
    width: 26px; height: 26px;
    display: grid; place-items: center;
    border-radius: 6px;
    font-size: 14px;
    background: rgba(74,149,181,0.16);
    border: 1px solid rgba(74,149,181,0.25);
  }
  .cmdk-icon.user { background: rgba(155,123,212,0.16); border-color: rgba(155,123,212,0.25); }
  .cmdk-icon.data { background: rgba(243,156,18,0.16); border-color: rgba(243,156,18,0.25); }
  .cmdk-text { min-width: 0; }
  .cmdk-text .t {
    font-size: 13px; font-weight: 500;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .cmdk-text .s {
    font-size: 11px; color: #5C6B7C;
    margin-top: 2px;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .cmdk-hint {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    color: #5C6B7C;
    max-width: 180px;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .cmdk-foot {
    display: flex; gap: 18px;
    padding: 10px 16px;
    border-top: 1px solid rgba(255,255,255,0.08);
    font-size: 11px;
    color: #5C6B7C;
    background: rgba(0,0,0,0.2);
    kbd {
      display: inline-block;
      padding: 1px 5px;
      margin-right: 4px;
      background: rgba(255,255,255,0.08);
      border-radius: 3px;
      font-family: 'JetBrains Mono', monospace;
      font-size: 10px;
      color: #C8D1DB;
    }
  }
  .fade-enter-active, .fade-leave-active { transition: opacity 120ms ease; }
  .fade-enter-from, .fade-leave-to { opacity: 0; }
</style>
